<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from '../provider.js';
import BaseAttachmentBubble from './BaseAttachment.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

// A mesma medida serve ao circulo reservado pelo cartao e ao Avatar dentro
// dele — se as duas divergirem, a foto sai cortada ou sobra fundo em volta.
const AVATAR_SIZE = 56;

const { attachments } = useMessageContext();

const { t } = useI18n();

const attachment = computed(() => {
  return attachments.value[0];
});

const phoneNumber = computed(() => {
  return attachment.value?.fallbackTitle ?? '';
});

// O retrato guardado no anexo e a fonte do cartao: e o que o contato era no
// momento do envio. `name` e o campo escrito pelo Zoraxy; `firstName`/
// `lastName` cobrem os cartoes que chegaram pelo Telegram, que so gravam essas
// duas partes. Ver Messages::MessageBuilder#process_contact_card.
const contactName = computed(() => {
  const { meta } = attachment.value ?? {};
  const { name, firstName, lastName } = meta ?? {};
  return (
    name?.trim() ||
    `${firstName ?? ''} ${lastName ?? ''}`.trim() ||
    phoneNumber.value
  );
});

// Foto do contato tal como estava no envio. Sem ela o Avatar desenha as
// iniciais, ocupando exatamente o mesmo espaco — o cartao nao muda de tamanho.
const contactThumbnail = computed(() => {
  return attachment.value?.meta?.thumbnail || '';
});

const formattedPhoneNumber = computed(() => {
  return phoneNumber.value.replace(/\s|-|[A-Za-z]/g, '');
});

const rawPhoneNumber = computed(() => {
  return phoneNumber.value.replace(/\D/g, '');
});

// "Mensagem", como no WhatsApp: o botao abre a conversa com este contato.
//
// Quem executa e a ChatList, atraves do fluxo unico de iniciar conversa
// (AceleresStartConversationFlow): ele resolve/cria o contato, checa se o lead
// ja e de outro vendedor, deixa escolher a conexao e navega. Uma segunda copia
// dessa regra dentro de uma bolha sairia de sincronia na primeira mudanca.
function openChatWithContact() {
  emitter.emit(BUS_EVENTS.ACELERES_OPEN_CHAT_WITH_PHONE, {
    phone: `+${rawPhoneNumber.value}`,
  });
}

const action = computed(() => ({
  label: t('CONVERSATION.MESSAGE_CONTACT'),
  onClick: openChatWithContact,
}));
</script>

<template>
  <BaseAttachmentBubble
    icon="i-teenyicons-user-circle-solid"
    sender-translation-key="CONVERSATION.SHARED_ATTACHMENT.CONTACT"
    :title="contactName"
    :content="phoneNumber"
    :action="formattedPhoneNumber ? action : null"
    :icon-size="AVATAR_SIZE"
    title-class="text-[17px] font-semibold leading-tight"
    content-class="text-[14px] leading-tight"
  >
    <!-- O avatar ocupa exatamente o circulo que o BaseAttachment reserva para
         o icone: nome e telefone ficam alinhados igual, com ou sem foto. -->
    <template #icon>
      <Avatar
        :name="contactName"
        :src="contactThumbnail"
        :size="AVATAR_SIZE"
        rounded-full
      />
    </template>
  </BaseAttachmentBubble>
</template>
